/**
 * Pipeline Health Dashboard — Admin component showing pipeline operational status.
 *
 * Phase 6 Task 6-2f: Displays health across ingestion, drift, model, review,
 * and performance subsystems. Shows trend and alert history.
 *
 * API endpoints used:
 *   GET /api/v1/admin/pipeline/health        — current snapshot
 *   GET /api/v1/admin/pipeline/health/alerts — alert history (stored snapshots)
 *   GET /api/v1/admin/pipeline/runs          — recent ingestion runs feed (limit + before cursor)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../utils/api';

const STATUS_COLORS = {
  healthy: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-700 dark:text-green-400', dot: 'bg-green-500' },
  degraded: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', text: 'text-yellow-700 dark:text-yellow-400', dot: 'bg-yellow-500' },
  unhealthy: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-700 dark:text-red-400', dot: 'bg-red-500' },
  unknown: { bg: 'bg-neutral-50 dark:bg-neutral-800', border: 'border-neutral-200 dark:border-neutral-700', text: 'text-neutral-500 dark:text-neutral-400', dot: 'bg-neutral-400' },
};

const ALERT_COLORS = {
  normal: 'text-green-600 dark:text-green-400',
  warning: 'text-yellow-600 dark:text-yellow-400',
  critical: 'text-red-600 dark:text-red-400',
};

function StatusBanner({ status, issues }) {
  const colors = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} p-4 mb-6`}>
      <div className="flex items-center gap-3">
        <span className={`inline-block w-3 h-3 rounded-full ${colors.dot}`} />
        <h2 className={`text-lg font-semibold ${colors.text}`}>
          Pipeline Status: {status.charAt(0).toUpperCase() + status.slice(1)}
        </h2>
      </div>
      {issues && issues.length > 0 && (
        <ul className="mt-2 ml-6 list-disc text-sm text-neutral-600 dark:text-neutral-300">
          {issues.map((issue, i) => <li key={i}>{issue}</li>)}
        </ul>
      )}
    </div>
  );
}

function SubsystemCard({ title, icon, items }) {
  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">
        <span>{icon}</span> {title}
      </h3>
      <dl className="space-y-2">
        {items.map(({ label, value, color }) => (
          <div key={label} className="flex justify-between text-sm">
            <dt className="text-neutral-500 dark:text-neutral-400">{label}</dt>
            <dd className={`font-medium ${color || 'text-neutral-900 dark:text-neutral-100'}`}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function AlertHistoryTable({ alerts }) {
  if (!alerts || alerts.length === 0) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">No alerts in this period.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Time</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Status</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Issues</th>
          </tr>
        </thead>
        <tbody>
          {alerts.map((alert, i) => {
            const colors = STATUS_COLORS[alert.overall_status] || STATUS_COLORS.unknown;
            return (
              <tr key={i} className="border-b border-neutral-100 dark:border-neutral-800">
                <td className="py-2 px-3 text-neutral-600 dark:text-neutral-300">
                  {alert.measured_at ? new Date(alert.measured_at).toLocaleString() : '—'}
                </td>
                <td className={`py-2 px-3 font-medium ${colors.text}`}>
                  {alert.overall_status}
                </td>
                <td className="py-2 px-3 text-neutral-600 dark:text-neutral-300">
                  {Array.isArray(alert.issues)
                    ? alert.issues.join('; ')
                    : typeof alert.issues === 'string'
                      ? alert.issues
                      : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const FRESHNESS_STATUS_COLORS = {
  ok: 'text-green-600 dark:text-green-400',
  warn: 'text-yellow-600 dark:text-yellow-400',
  critical: 'text-red-600 dark:text-red-400',
  suppressed: 'text-neutral-400 dark:text-neutral-500',
};

function SourceFreshnessTable({ sources }) {
  if (!sources || sources.length === 0) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">No per-source freshness data.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Source</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">SLA</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Last Refresh</th>
            <th className="text-right py-2 px-3 text-neutral-500 dark:text-neutral-400">Age (d)</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Status</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Next Expected</th>
          </tr>
        </thead>
        <tbody>
          {sources.map((s) => {
            const suppressed = s.blocked || s.manual;
            const label = s.blocked ? 'blocked' : (s.manual ? 'manual' : s.status);
            return (
              <tr key={s.source} className="border-b border-neutral-100 dark:border-neutral-800">
                <td className="py-2 px-3 text-neutral-700 dark:text-neutral-200">{s.source}</td>
                <td className="py-2 px-3 text-neutral-500 dark:text-neutral-400">{s.sla?.interval || '—'}</td>
                <td className="py-2 px-3 text-neutral-600 dark:text-neutral-300 whitespace-nowrap">
                  {s.last_refresh ? new Date(s.last_refresh).toLocaleDateString() : 'never'}
                </td>
                <td className="py-2 px-3 text-right text-neutral-600 dark:text-neutral-300">
                  {s.age_days != null ? s.age_days : '—'}
                </td>
                <td className={`py-2 px-3 font-medium ${FRESHNESS_STATUS_COLORS[suppressed ? 'suppressed' : s.status] || ''}`}>
                  {label}{s.overdue ? ' · overdue' : ''}
                </td>
                <td className="py-2 px-3 text-neutral-500 dark:text-neutral-400 whitespace-nowrap">
                  {s.next_expected_run ? new Date(s.next_expected_run).toLocaleString() : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const RUN_STATUS_COLORS = {
  success: 'text-green-600 dark:text-green-400',
  skipped: 'text-neutral-500 dark:text-neutral-400',
  failed: 'text-red-600 dark:text-red-400',
  running: 'text-blue-600 dark:text-blue-400',
};

function RecentRunsTable({ runs }) {
  if (!runs || runs.length === 0) {
    return <p className="text-sm text-neutral-500 dark:text-neutral-400">No ingestion runs recorded yet.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-200 dark:border-neutral-700">
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Started</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Source</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Type</th>
            <th className="text-left py-2 px-3 text-neutral-500 dark:text-neutral-400">Status</th>
            <th className="text-right py-2 px-3 text-neutral-500 dark:text-neutral-400">Rows Δ</th>
            <th className="text-right py-2 px-3 text-neutral-500 dark:text-neutral-400">Duration</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((r, i) => (
            <tr key={r.run_id || i} className="border-b border-neutral-100 dark:border-neutral-800">
              <td className="py-2 px-3 text-neutral-600 dark:text-neutral-300 whitespace-nowrap">
                {r.started_at ? new Date(r.started_at).toLocaleString() : '—'}
              </td>
              <td className="py-2 px-3 text-neutral-700 dark:text-neutral-200">{r.source_id || '—'}</td>
              <td className="py-2 px-3 text-neutral-500 dark:text-neutral-400">{r.run_type || '—'}</td>
              <td className={`py-2 px-3 font-medium ${RUN_STATUS_COLORS[r.status] || 'text-neutral-600 dark:text-neutral-300'}`}>
                {r.status || '—'}
                {r.status === 'failed' && r.error_message ? (
                  <span className="block text-xs font-normal text-red-500 truncate max-w-xs" title={r.error_message}>
                    {r.error_message}
                  </span>
                ) : null}
              </td>
              <td className="py-2 px-3 text-right text-neutral-600 dark:text-neutral-300">
                {r.rows_changed != null ? r.rows_changed : (r.rows_extracted != null ? r.rows_extracted : '—')}
              </td>
              <td className="py-2 px-3 text-right text-neutral-500 dark:text-neutral-400">
                {r.duration_ms != null ? `${(r.duration_ms / 1000).toFixed(1)}s` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Sparkline({ points, threshold }) {
  // points: oldest→newest, each { v } with v in [0,1] (or null gap).
  const H = 44, W = 240;
  const valid = points.filter((p) => p.v != null);
  if (valid.length < 2) {
    return (
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        Not enough snapshots yet for a trend (need ≥2 — they accrue as the health-snapshot cron runs).
      </p>
    );
  }
  const n = points.length;
  const x = (i) => (n === 1 ? 0 : (i / (n - 1)) * W);
  const y = (v) => H - Math.max(0, Math.min(1, v)) * H;
  let d = '', started = false;
  points.forEach((p, i) => {
    if (p.v == null) return;
    d += `${started ? 'L' : 'M'}${x(i).toFixed(1)},${y(p.v).toFixed(1)} `;
    started = true;
  });
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height: H }} role="img" aria-label="Ingestion success trend">
      {threshold != null && (
        <line x1="0" y1={y(threshold)} x2={W} y2={y(threshold)} stroke="#f59e0b" strokeWidth="1" strokeDasharray="3" vectorEffect="non-scaling-stroke" />
      )}
      <path d={d.trim()} fill="none" stroke="#a100aa" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

export default function PipelineHealthDashboard() {
  const [health, setHealth] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alertHours, setAlertHours] = useState(24);
  const [runs, setRuns] = useState([]);
  const [runsExhausted, setRunsExhausted] = useState(false);
  const [runsLoadingMore, setRunsLoadingMore] = useState(false);
  const [trend, setTrend] = useState([]);

  const fetchHealth = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/pipeline/health');
      if (res.ok) {
        setHealth(await res.json());
        setError(null);
      } else {
        setError('Failed to load pipeline health');
      }
    } catch (e) {
      setError('Failed to connect to API');
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/v1/admin/pipeline/health/alerts?hours=${alertHours}`);
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch {
      // alerts are optional
    }
  }, [alertHours]);

  const fetchRuns = useCallback(async () => {
    try {
      const res = await apiFetch('/api/v1/admin/pipeline/runs?limit=10');
      if (res.ok) {
        const data = await res.json();
        const list = data.runs || [];
        setRuns(list);
        setRunsExhausted(list.length < 10);
      }
    } catch {
      // runs feed is optional
    }
  }, []);

  const fetchTrend = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/v1/admin/pipeline/health/trend?hours=${alertHours}`);
      if (res.ok) {
        const data = await res.json();
        setTrend(data.snapshots || []);
      }
    } catch {
      // trend is optional
    }
  }, [alertHours]);

  const loadOlderRuns = async () => {
    if (runs.length === 0) return;
    setRunsLoadingMore(true);
    try {
      const before = runs[runs.length - 1].started_at;
      const res = await apiFetch(
        `/api/v1/admin/pipeline/runs?limit=10&before=${encodeURIComponent(before)}`
      );
      if (res.ok) {
        const data = await res.json();
        const more = data.runs || [];
        setRuns((prev) => [...prev, ...more]);
        if (more.length < 10) setRunsExhausted(true);
      }
    } finally {
      setRunsLoadingMore(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchHealth(), fetchAlerts(), fetchTrend()]);
      setLoading(false);
    };
    load();
    // Runs feed loads once on mount (and on the manual Refresh button) — kept OUT
    // of the 60s interval so an auto-refresh never discards an in-progress
    // "Load older" pagination by replacing the accumulated list with page 1.
    fetchRuns();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [fetchHealth, fetchAlerts, fetchTrend, fetchRuns]);

  if (loading && !health) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100 mb-4">Pipeline Health</h1>
        <p className="text-neutral-500 dark:text-neutral-400">Loading pipeline health data...</p>
      </div>
    );
  }

  if (error && !health) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100 mb-4">Pipeline Health</h1>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">{error}</div>
      </div>
    );
  }

  const ing = health?.ingestion || {};
  const drift = health?.drift || {};
  const model = health?.model || {};
  const review = health?.review || {};
  const perf = health?.performance || {};
  const reg = health?.regulation || {};
  const freshness = health?.freshness || {};
  const coverage = health?.embedding_coverage || {};

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-neutral-800 dark:text-neutral-100">Pipeline Health Dashboard</h1>
        <button
          onClick={() => { fetchHealth(); fetchAlerts(); fetchRuns(); fetchTrend(); }}
          className="px-3 py-1.5 text-sm rounded-md border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-200"
        >
          Refresh
        </button>
      </div>

      <StatusBanner status={health?.overall_status || 'unknown'} issues={health?.issues} />

      {health?.overall_status === 'unknown' && !health?.ingestion && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-blue-700 mb-6">
          No health snapshots yet. The scheduler creates one every 15 minutes once the pipeline is running.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <SubsystemCard
          title="Ingestion"
          icon="📥"
          items={[
            { label: 'Success Rate (24h)', value: ing.success_rate_24h != null ? `${(ing.success_rate_24h * 100).toFixed(1)}%` : '—' },
            { label: 'Consecutive Failures', value: ing.consecutive_failures ?? 0, color: ing.consecutive_failures > 3 ? 'text-red-600' : undefined },
            { label: 'Dead Letters', value: ing.dead_letter_count ?? 0, color: ing.dead_letter_count > 10 ? 'text-yellow-600' : undefined },
            { label: 'Avg Duration', value: ing.avg_duration_ms != null ? `${(ing.avg_duration_ms / 1000).toFixed(1)}s` : '—' },
            { label: 'Last Success', value: ing.last_success ? new Date(ing.last_success).toLocaleString() : '—' },
            {
              label: 'Corpus Fresh',
              value: freshness.corpus_fresh == null ? '—' : (freshness.corpus_fresh ? 'Yes' : `No (${freshness.overdue_count ?? 0} overdue)`),
              color: freshness.corpus_fresh === false ? 'text-yellow-600' : (freshness.corpus_fresh ? 'text-green-600' : undefined),
            },
            {
              label: 'Embedding Coverage',
              value: coverage.total != null ? `${coverage.embedded ?? 0}/${coverage.total} (${((coverage.coverage ?? 0) * 100).toFixed(0)}%)` : '—',
              color: coverage.gap ? 'text-yellow-600' : undefined,
            },
            {
              label: 'Retrain Pending',
              value: freshness.retrain_pending == null ? '—' : (freshness.retrain_pending ? 'Yes' : 'No'),
              color: freshness.retrain_pending ? 'text-yellow-600' : undefined,
            },
          ]}
        />

        <SubsystemCard
          title="Drift Monitoring"
          icon="📊"
          items={[
            { label: 'Overall Alert', value: drift.overall_alert || 'normal', color: ALERT_COLORS[drift.overall_alert] || ALERT_COLORS.normal },
            { label: 'Active Alerts', value: drift.active_alert_count ?? 0 },
            { label: 'Last Check', value: drift.last_check ? new Date(drift.last_check).toLocaleString() : '—' },
          ]}
        />

        <SubsystemCard
          title="ML Model"
          icon="🤖"
          items={[
            { label: 'Status', value: model.ready ? 'Loaded' : 'Not Available', color: model.ready ? 'text-green-600' : 'text-red-600' },
            { label: 'Version', value: model.version || '—' },
            { label: 'Last Retrained', value: model.last_retrained ? new Date(model.last_retrained).toLocaleString() : '—' },
          ]}
        />

        <SubsystemCard
          title="Review Queue"
          icon="📋"
          items={[
            { label: 'Pending Items', value: review.queue_depth ?? 0, color: review.queue_depth > 50 ? 'text-yellow-600' : undefined },
            { label: 'Stale Items', value: review.stale_count ?? 0, color: review.stale_count > 0 ? 'text-yellow-600' : undefined },
            { label: 'Overturn Rate', value: review.overturn_rate != null ? `${(review.overturn_rate * 100).toFixed(1)}%` : '—' },
          ]}
        />

        <SubsystemCard
          title="Performance"
          icon="⚡"
          items={[
            { label: 'Throughput (1h)', value: perf.throughput_1h != null ? `${perf.throughput_1h}/min` : '—' },
            { label: 'Avg Latency', value: perf.avg_latency_ms != null ? `${perf.avg_latency_ms}ms` : '—', color: perf.avg_latency_ms > 5000 ? 'text-red-600' : undefined },
          ]}
        />

        <SubsystemCard
          title="Regulation Changes"
          icon="📜"
          items={[
            { label: 'Open Changes', value: reg.open_count ?? 0, color: reg.open_count > 10 ? 'text-yellow-600' : undefined },
            { label: 'Oldest Open', value: reg.oldest_open_days != null ? `${reg.oldest_open_days}d` : '—' },
          ]}
        />
      </div>

      {/* Per-source freshness (cadence-aware SLA; blocked/manual sources suppressed) */}
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Source Freshness</h3>
          <span className="text-xs text-neutral-400 dark:text-neutral-500">
            corpus fresh: {freshness.corpus_fresh == null ? '—' : (freshness.corpus_fresh ? 'yes' : `no · ${freshness.overdue_count ?? 0} overdue`)}
          </span>
        </div>
        <SourceFreshnessTable sources={freshness.per_source} />
      </div>

      {/* Ingestion success trend (sparkline over the selected window) */}
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Ingestion Success Trend</h3>
          <span className="text-xs text-neutral-400 dark:text-neutral-500">
            last {alertHours}h · dashed line = 80% target
          </span>
        </div>
        <Sparkline
          points={[...trend].reverse().map((s) => ({ v: s.ingestion_success_rate_24h }))}
          threshold={0.8}
        />
      </div>

      {/* Recent Ingestion — "what's being ingested" (recent pipeline_runs) */}
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4 mb-6">
        <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Recent Ingestion</h3>
        <RecentRunsTable runs={runs} />
        {runs.length > 0 && !runsExhausted && (
          <div className="mt-3 text-center">
            <button
              onClick={loadOlderRuns}
              disabled={runsLoadingMore}
              className="px-3 py-1.5 text-sm rounded-md border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 text-neutral-700 dark:text-neutral-200 disabled:opacity-50"
            >
              {runsLoadingMore ? 'Loading…' : 'Load older'}
            </button>
          </div>
        )}
      </div>

      {/* Alert History */}
      <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">Alert History</h3>
          <select
            value={alertHours}
            onChange={(e) => setAlertHours(Number(e.target.value))}
            className="text-sm border border-neutral-300 dark:border-neutral-600 rounded px-2 py-1 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200"
          >
            <option value={6}>Last 6 hours</option>
            <option value={24}>Last 24 hours</option>
            <option value={48}>Last 48 hours</option>
            <option value={168}>Last 7 days</option>
          </select>
        </div>
        <AlertHistoryTable alerts={alerts} />
      </div>

      {health?.measured_at && (
        <p className="mt-4 text-xs text-neutral-400 dark:text-neutral-500">
          Last measured: {new Date(health.measured_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}
